import moment from 'moment';
import numeral from 'src/utilities/numeral';
import {
  getStringDateTimeFromURI,
  validateDate,
  validateEmails,
} from 'src/utilities/validator';

function replaceURIToObj(params) {
  return params.replace('?', '').replace(/&/g, '","').replace(/=/g, '":"');
}

export function paramsToObj(params) {
  try {
    return params.length > 0
      ? JSON.parse(`{"${replaceURIToObj(decodeURI(params))}"}`)
      : {};
  } catch (error) {
    return {};
  }
}

export function objToParams(obj) {
  let str = '';
  for (const key in obj) {
    if (str !== '') {
      str += '&';
    }
    if (validateEmails([obj[key]])) {
      str += `${key}=${obj[key]}`;
    } else if (validateDate(obj[key])) {
      str += `${key}=${getStringDateTimeFromURI(obj[key])}`;
    } else {
      str += `${key}=${encodeURIComponent(obj[key])}`;
    }
  }

  return str;
}

export const formDataProductIsFilled = (data, isDeleteIndex, index) => {
  const dataSet = isDeleteIndex ? data : [data[index]];
  const filterData = dataSet.filter(
    item => item.description || item.quantity || item.price_per_item,
  );
  return filterData.length > 0;
};

export const unixToDate = unix => {
  const date = new Date(unix * 1000);
  return date;
};

// e.g: pejetaja returns pejetaja, pejetaja~pejetmeong returns pejetmeong.
export const usernameWithoutParent = username => {
  return username.indexOf('~') === -1
    ? username
    : (username || '').split('~')[1];
};

export const normalizeAmount = amount => {
  if (typeof amount === 'string') {
    return amount.replace(/[^0-9]/g, '');
  }
  return amount;
};

export const formatDateToString = date => {
  return moment(date).format('YYYY-MM-DD');
};

export const formatDateToStringSlash = date => {
  return moment(date).format('YYYY/MM/DD');
};

export const formatTimeWithSecondsToString = date => {
  return moment(date).format('HH:mm:ss');
};

export const formatTimeWithSecondsToStringLocal = date => {
  return moment.utc(date).local().format('HH:mm:ss');
};

export const formatDateWithSpaceToString = date => {
  return moment(date).format('DD MMMM YYYY');
};

export const formatDateWithSpaceToStringLocal = date => {
  return moment.utc(date).local().format('DD MMMM YYYY');
};

export const formatStringToDate = date => {
  return moment(date, 'YYYY-MM-DD').toDate();
};

export const formatDateTimeToString = date => {
  return moment(date).format('YYYY-MM-DD HH:mm');
};

export const formatDateTimeWithSpaceToString = date => {
  return moment(date).format('DD MMMM YYYY HH:mm');
};

export const formatDateTimeWithSpaceToStringLocal = date => {
  return moment.utc(date).local().format('DD MMMM YYYY HH:mm');
};

export const formatDateTimeWithSecondsToString = date => {
  return moment(date).format('YYYY-MM-DD HH:mm:ss');
};

export const formatLocalDateToUTC = date => {
  return moment(date).utc().format('yyyy-MM-DD HH:mm:ss');
};

export const formatUTCStringToLocalDate = utcString => {
  return moment.utc(utcString).local().toDate();
};

export const formatUTCDatetoLocalString = date => {
  return moment.utc(date).local().format('DD MMMM YYYY HH:mm');
};

export const formatCurrency = value => numeral(value).format('$(0,0)');

const getExportValuePrefix = value => {
  if (value == 0) return '';
  return value > 0 ? '+' : '-';
};

export const formatCurrencyWithNegative = value =>
  getExportValuePrefix(value) + numeral(Math.abs(value)).format('$(0,0)');

export const formatCurrencyInternational = (value, currencyCode) => {
  return currencyCode + ' ' + numeral(value).format('0,0');
};

export const formatCurrencyWithOperatorSymbol = (value, type, lang = 'id') => {
  const formatter = new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  });
  return `${type ? `${type} ` : ''}${formatter.format(Math.abs(value))}`;
};

export const convertToNumber = value => {
  const val = numeral(value).value();
  return val === null ? 0 : val;
};

export const replaceSpaceWithEmptyString = str => {
  if (typeof str === 'string') {
    return str.replace(/\s/g, '');
  }
  return str;
};

export function convertFileArrayBufferTo2dArray(arrayBuffer) {
  return import('xlsx').then(XLSX => {
    const data = new Uint8Array(arrayBuffer);
    const workbook = XLSX.read(data, { type: 'array' });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const result = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      blankrows: false,
      raw: true,
    });
    return result.map(row => row.map(cell => String(cell)));
  });
}

// This function used to check the validity of amount input
// If user input the amount with the "." or "," as a thousand separator
// Ex: 2.000 (become 2000, because the "." is valid)
// EX: 2,00 (become 2, because the "," is not valid as a thousand separator)
export const parseAmount = amount => {
  const dot = '.';
  const comma = ',';
  const isThousandGroupDigit = /\b\d{3}\b/;
  let parsedAmount = amount;

  if (amount.includes(comma)) {
    const indexComma = amount.indexOf(comma);
    parsedAmount = amount.substring(0, indexComma);
  }

  if (parsedAmount.includes(dot)) {
    let indexDot = parsedAmount.indexOf(dot);
    let nextDot = parsedAmount.indexOf(dot, indexDot + 1);
    while (nextDot > 0) {
      if (
        isThousandGroupDigit.test(parsedAmount.substring(indexDot + 1, nextDot))
      ) {
        indexDot = nextDot;
        nextDot = parsedAmount.indexOf(dot, nextDot + 1);
      } else {
        return parsedAmount.substring(0, indexDot);
      }
    }

    if (isThousandGroupDigit.test(parsedAmount.substring(indexDot + 1))) {
      return parsedAmount;
    }

    return parsedAmount.substring(0, indexDot);
  }

  return parsedAmount;
};

export const fileAccepts = (file, acceptedFiles) => {
  if (!acceptedFiles) return false;
  const acceptedFilesArray = acceptedFiles.split(',');

  const fileName = file.name || '';
  const fileType = (file.type || '').toLowerCase();
  const baseFileType = fileType.replace(/\/.*$/, '');

  return acceptedFilesArray.some(type => {
    const validType = type.trim().toLowerCase();

    if (validType[0] === '.') {
      return fileName.toLowerCase().endsWith(validType);
    }
    if (validType.endsWith('/*')) {
      return baseFileType === validType.replace(/\/.*$/, '');
    }
    return fileType === validType;
  });
};

export const excelDateToJsDate = serial => {
  if (!serial) {
    return null;
  }
  const utc_days = Math.floor(serial - 25569);
  const utc_value = utc_days * 86400;
  const date_info = new Date(utc_value * 1000);

  const fractional_day = serial - Math.floor(serial) + 0.0000001;

  let total_seconds = Math.floor(86400 * fractional_day);

  const seconds = total_seconds % 60;

  total_seconds -= seconds;

  const hours = Math.floor(total_seconds / (60 * 60));
  const minutes = Math.floor(total_seconds / 60) % 60;
  const date = new Date(
    date_info.getFullYear(),
    date_info.getMonth(),
    date_info.getDate(),
    hours,
    minutes,
    seconds,
  );

  return moment(date).isValid() ? date : null;
};

export const isValidDataUrl = url => {
  const dataUrlRegex = new RegExp(
    "^data:([a-z]+\\/[a-z0-9-+.]+(;[a-z0-9-.!#$%*+.{}|~`]+=[a-z0-9-.!#$%*+.{}()|~`]+)*)?(;base64)?,([a-z0-9!$&',()*+;=\\-._~:@\\/?%\\s<>]*?)$",
    'i',
  );
  return dataUrlRegex.test((url || '').trim());
};

export const getLocalDateFromWib = date => {
  // set to utc from WIB
  date.setHours(date.getHours() - 7);

  const nowUtc = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
  );

  return new Date(nowUtc);
};

export const getPageCountTable = (totalData, limit) => {
  return Math.ceil(totalData / limit);
};

export const getNewDataTable = (data, pageSize) => {
  if (data?.length ?? 0 > pageSize) {
    return data.slice(0, pageSize);
  }
  return data;
};
